import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../config/axiosClient";
import ReactMarkdown from "react-markdown";
import toast from "react-hot-toast";
import CodeMirror from "@uiw/react-codemirror";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { EditorView, keymap } from "@codemirror/view";
import { indentUnit } from "@codemirror/language";
import { EditorState } from "@codemirror/state";
import { closeBrackets } from "@codemirror/autocomplete";
import { createTheme } from "@uiw/codemirror-themes";
import { tags as t } from "@lezer/highlight";

import {
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
} from "../auth/AuthActions";
import Config from "../../config/Config.js";

const THEME_STORAGE_KEY = "i7_editor_theme";

const EDITOR_THEMES = {
  midnight: {
    label: "Midnight", swatch: "#000000",
    theme: createTheme({
      theme: "dark",
      settings: {
        background: "#000000e6", foreground: "#c9d1d9", caret: "#59c6c1",
        selection: "#59c6c133", selectionMatch: "#59c6c122", lineHighlight: "#ffffff08",
        gutterBackground: "transparent", gutterForeground: "#475569", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#59c6c1", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#59c6c1", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#59c6c1", fontWeight: "600" },
        { tag: t.strong, color: "#e2e8f0", fontWeight: "700" },
        { tag: t.emphasis, color: "#c4b5fd", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#c9d1d9" },
        { tag: t.url, color: "#7dd3fc", textDecoration: "underline" },
        { tag: t.link, color: "#7dd3fc" },
        { tag: t.labelName, color: "#7dd3fc" },
        { tag: [t.processingInstruction, t.inserted], color: "#f0abfc" },
        { tag: t.strikethrough, textDecoration: "line-through", color: "#64748b" },
        { tag: t.quote, color: "#94a3b8", fontStyle: "italic" },
        { tag: t.list, color: "#fbbf24" },
        { tag: [t.keyword], color: "#f472b6" },
        { tag: [t.string], color: "#a5f3fc" },
        { tag: [t.comment], color: "#64748b" },
        { tag: [t.number], color: "#fbbf24" },
      ],
    }),
  },
  ocean: {
    label: "Ocean", swatch: "#1a1b2e",
    theme: createTheme({
      theme: "dark",
      settings: {
        background: "#1a1b2e", foreground: "#a6accd", caret: "#82aaff",
        selection: "#82aaff22", selectionMatch: "#82aaff15", lineHighlight: "#ffffff06",
        gutterBackground: "transparent", gutterForeground: "#4e5579", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#82aaff", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#82aaff", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#82aaff", fontWeight: "600" },
        { tag: t.strong, color: "#eeffff", fontWeight: "700" },
        { tag: t.emphasis, color: "#c792ea", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#a6accd" },
        { tag: t.url, color: "#89ddff", textDecoration: "underline" },
        { tag: t.link, color: "#89ddff" },
        { tag: t.quote, color: "#676e95", fontStyle: "italic" },
        { tag: t.list, color: "#ffcb6b" },
        { tag: [t.keyword], color: "#c792ea" },
        { tag: [t.string], color: "#c3e88d" },
        { tag: [t.comment], color: "#4e5579" },
        { tag: [t.number], color: "#f78c6c" },
      ],
    }),
  },
  ember: {
    label: "Ember", swatch: "#1c1210",
    theme: createTheme({
      theme: "dark",
      settings: {
        background: "#1c1210", foreground: "#d4b8a0", caret: "#ff9e64",
        selection: "#ff9e6422", selectionMatch: "#ff9e6415", lineHighlight: "#ffffff06",
        gutterBackground: "transparent", gutterForeground: "#5c4033", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#ff9e64", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#ff9e64", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#ff9e64", fontWeight: "600" },
        { tag: t.strong, color: "#f0e0d0", fontWeight: "700" },
        { tag: t.emphasis, color: "#f7768e", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#d4b8a0" },
        { tag: t.url, color: "#73daca", textDecoration: "underline" },
        { tag: t.quote, color: "#8a6550", fontStyle: "italic" },
        { tag: t.list, color: "#e0af68" },
        { tag: [t.keyword], color: "#bb9af7" },
        { tag: [t.string], color: "#9ece6a" },
        { tag: [t.comment], color: "#5c4033" },
        { tag: [t.number], color: "#ff9e64" },
      ],
    }),
  },
  paper: {
    label: "Paper", swatch: "#fafaf9",
    theme: createTheme({
      theme: "light",
      settings: {
        background: "#fafaf9", foreground: "#3c3836", caret: "#076678",
        selection: "#07667822", selectionMatch: "#07667815", lineHighlight: "#00000006",
        gutterBackground: "transparent", gutterForeground: "#a89984", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#076678", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#076678", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#076678", fontWeight: "600" },
        { tag: t.strong, color: "#1d2021", fontWeight: "700" },
        { tag: t.emphasis, color: "#8f3f71", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#3c3836" },
        { tag: t.url, color: "#076678", textDecoration: "underline" },
        { tag: t.quote, color: "#7c6f64", fontStyle: "italic" },
        { tag: t.list, color: "#b57614" },
        { tag: [t.keyword], color: "#9d0006" },
        { tag: [t.string], color: "#79740e" },
        { tag: [t.comment], color: "#a89984" },
        { tag: [t.number], color: "#8f3f71" },
      ],
    }),
  },
};

const themeKeys = Object.keys(EDITOR_THEMES);

const cmBaseExtensions = [
  markdown({ base: markdownLanguage, codeLanguages: languages }),
  EditorView.lineWrapping,
  closeBrackets(),
  indentUnit.of("    "),
  EditorState.tabSize.of(4),
];

export default function CvChange() {
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
  const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

  const [cv, setCv] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [split, setSplit] = useState(50);
  const dragging = useRef(false);
  const initialCv = useRef("");
  const extendedUserIdRef = useRef(null);

  const [themeKey, setThemeKey] = useState(() => localStorage.getItem(THEME_STORAGE_KEY) || "midnight");
  const activeTheme = EDITOR_THEMES[themeKey]?.theme || EDITOR_THEMES.midnight.theme;

  const cycleTheme = useCallback(() => {
    setThemeKey((prev) => {
      const idx = themeKeys.indexOf(prev);
      const next = themeKeys[(idx + 1) % themeKeys.length];
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);

  // --- fetch ---
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${Config().extendedUserUrl}?user=${user.pk}`);
        if (!active) return;
        const data = res.data;
        const results = Array.isArray(data) ? data : data.results || [];
        const d = results[0] || {};
        extendedUserIdRef.current = d.id || null;
        setCv(d.cv || "");
        initialCv.current = d.cv || "";
      } catch (err) {
        console.error("CV Fetch Error:", err);
        toast.error("Couldn't load CV.");
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => { active = false; };
  }, [user.pk]);

  // --- save ---
  const save = useCallback(async () => {
    if (cv === initialCv.current) {
      toast("No changes to save.");
      return;
    }
    try {
      setLoading(true);
      await axios.patch(
        `${Config().extendedUserUrl}${extendedUserIdRef.current}/`,
        { cv },
        { headers: { Authorization: `Token ${auth.token}` } }
      );
      initialCv.current = cv;
      toast.success("CV saved");
    } catch (err) {
      console.error("CV Save Error:", err);
      toast.error("Save failed.");
    } finally {
      setLoading(false);
    }
  }, [cv, user.pk, auth.token]);

  // --- Ctrl+S ---
  const saveRef = useRef(save);
  useEffect(() => { saveRef.current = save; }, [save]);

  const cmExtensions = useMemo(() => [
    ...cmBaseExtensions,
    keymap.of([{
      key: "Mod-s",
      run: () => { saveRef.current(); return true; },
    }]),
  ], []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // --- drag to resize ---
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const container = document.getElementById("cv-editor");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.min(75, Math.max(25, (x / rect.width) * 100));
      setSplit(pct);
    };
    const stop = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
    };
  }, []);

  if (fetching) {
    return <div style={{ padding: 32, color: "var(--muted)", fontSize: 13 }}>Loading…</div>;
  }

  return (
    <div
      id="cv-editor"
      style={{
        display: "grid",
        gridTemplateColumns: `${split}% 6px ${100 - split}%`,
        height: "100%",
        gap: 0,
        overflow: "hidden",
        background: "var(--bg)",
      }}
    >
      {/* LEFT: Editor */}
      <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Toolbar */}
        <div style={{
          flexShrink: 0, padding: "6px 16px",
          borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
            CV <span style={{ color: "#475569", fontWeight: 400 }}>— Markdown</span>
          </span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            {/* Theme swatch */}
            <button
              type="button"
              onClick={cycleTheme}
              title={`Theme: ${EDITOR_THEMES[themeKey]?.label || themeKey}`}
              style={{
                width: 18, height: 18, borderRadius: "50%", border: "2px solid #475569",
                background: EDITOR_THEMES[themeKey]?.swatch || "#000",
                cursor: "pointer", padding: 0,
              }}
            />
            <button
              type="button"
              onClick={() => saveRef.current()}
              disabled={loading}
              style={{
                background: "#59c6c1", border: "none", borderRadius: 6,
                color: "#0f172a", fontWeight: 600, fontSize: 12,
                padding: "4px 16px", cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* CodeMirror */}
        <div style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden", margin: "8px 16px 12px", border: "1px solid #334155", borderRadius: 8 }}>
          <CodeMirror
            value={cv}
            onChange={setCv}
            theme={activeTheme}
            extensions={cmExtensions}
            placeholder="# Your Name&#10;&#10;## Experience&#10;&#10;- Role at Company&#10;&#10;## Skills&#10;&#10;- Python, React, …"
            height="100%"
            style={{ height: "100%", fontSize: 14 }}
            basicSetup={{
              lineNumbers: false,
              foldGutter: false,
              highlightActiveLine: true,
              highlightSelectionMatches: true,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: false,
              crosshairCursor: false,
              rectangularSelection: false,
              indentOnInput: true,
            }}
          />
        </div>
      </div>

      {/* Gutter */}
      <div
        style={{ cursor: "col-resize", background: "var(--border)" }}
        onMouseDown={() => (dragging.current = true)}
        title="Drag to resize"
      />

      {/* RIGHT: Preview */}
      <div style={{ overflowY: "auto", padding: "24px 48px", background: "var(--bg)" }}>
        <div className="custom-body-markdown" style={{ fontSize: 14, lineHeight: 1.7 }}>
          <ReactMarkdown>{cv || "_Start typing your CV…_"}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
